/**
 * Admin API Service
 * Handles admin-specific API calls to backend
 */

import supabase from '../supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Get authentication headers with JWT token
 * @returns {Promise<Object>} Headers object with Authorization token
 */
async function getAuthHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  return {
    'Authorization': token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json'
  };
}

/**
 * Get all users with activity summaries
 * Admin endpoint: GET /api/admin/users
 *
 * @returns {Promise<Object>} Response with users array
 * Response format:
 * {
 *   success: true,
 *   data: {
 *     users: [
 *       {
 *         id: UUID,
 *         email: string,
 *         created_at: timestamp,
 *         activity: {
 *           receiptUploads: number,
 *           manualEntries: number,
 *           statementUploads: number,
 *           aiChats: number,
 *           analyticsViews: number,
 *           totalExpenses: number
 *         }
 *       },
 *       ...
 *     ],
 *     count: number
 *   }
 * }
 */
export async function getAllUsers() {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/admin/users`, {
    method: 'GET',
    headers: authHeaders
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch users');
  }

  return response.json();
}

/**
 * Get activity details for a specific user
 * Admin endpoint: GET /api/admin/users/:userId/activity
 *
 * @param {string} userId - User ID to get activity for
 * @returns {Promise<Object>} User activity summary
 * Response format:
 * {
 *   success: true,
 *   data: {
 *     userId: UUID,
 *     receiptUploads: number,
 *     manualEntries: number,
 *     statementUploads: number,
 *     aiChats: number,
 *     analyticsViews: number,
 *     totalExpenses: number
 *   }
 * }
 */
export async function getUserActivity(userId) {
  if (!userId) {
    throw new Error('userId parameter is required');
  }

  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/admin/users/${userId}/activity`, {
    method: 'GET',
    headers: authHeaders
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch user activity');
  }

  return response.json();
}

/**
 * Get exchange rate diagnostics (admin only)
 * Admin endpoint: GET /api/admin/exchange-rate-diagnostics
 *
 * @param {string} from - Base currency code
 * @param {string} to - Target currency code
 * @returns {Promise<Object>} Diagnostics payload
 */
export async function getExchangeRateDiagnostics(from = 'MYR', to = 'SGD', refresh = false) {
  const authHeaders = await getAuthHeaders();
  const params = new URLSearchParams({
    from: from.toUpperCase(),
    to: to.toUpperCase(),
    refresh: refresh ? 'true' : 'false'
  });

  const response = await fetch(`${API_URL}/api/admin/exchange-rate-diagnostics?${params.toString()}`, {
    method: 'GET',
    headers: authHeaders
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch exchange rate diagnostics');
  }

  return response.json();
}

/**
 * Get all exchange rates for a base currency (admin only)
 * Admin endpoint: GET /api/admin/exchange-rates
 *
 * @param {string} base - Base currency code (default: SGD)
 * @param {boolean} refresh - Force refresh from API (default: false)
 * @returns {Promise<Object>} Exchange rates with metadata
 */
export async function getAllExchangeRates(base = 'SGD', refresh = false) {
  const authHeaders = await getAuthHeaders();
  const params = new URLSearchParams({
    base: base.toUpperCase(),
    refresh: refresh ? 'true' : 'false'
  });

  const url = `${API_URL}/api/admin/exchange-rates?${params.toString()}`;
  console.log(`📡 [adminApi] Fetching exchange rates from: ${url}`);

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders
  });

  console.log(`📡 [adminApi] Response status: ${response.status}, ok: ${response.ok}`);

  let data;
  try {
    data = await response.json();
    console.log(`📡 [adminApi] Response data:`, data);
  } catch (parseError) {
    console.error(`❌ [adminApi] Failed to parse response JSON:`, parseError);
    throw new Error('Server returned invalid JSON response');
  }

  if (!response.ok) {
    const errorMessage = data?.message || data?.error || 'Failed to fetch exchange rates';
    console.error(`❌ [adminApi] Error response:`, errorMessage);
    throw new Error(errorMessage);
  }

  // Validate that response has the expected structure
  if (!data.data || !Array.isArray(data.data.rates)) {
    console.error(`❌ [adminApi] Invalid response structure:`, data);
    throw new Error('Invalid response structure: missing data.rates array');
  }

  console.log(`✅ [adminApi] Successfully fetched ${data.data.rates.length} rates`);
  return data;
}

/**
 * Get list of expenses with currency conversion issues
 * Admin endpoint: GET /api/admin/currency-issues
 *
 * @returns {Promise<Object>} List of expenses with issues
 */
export async function getCurrencyIssues() {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/admin/currency-issues`, {
    method: 'GET',
    headers: authHeaders
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch currency issues');
  }

  return response.json();
}

/**
 * Recalculate currency conversions for all affected expenses
 * Admin endpoint: POST /api/admin/recalculate-currency
 *
 * @returns {Promise<Object>} Result with update count
 */
export async function recalculateCurrencyConversions() {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/admin/recalculate-currency`, {
    method: 'POST',
    headers: authHeaders
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to recalculate currencies');
  }

  return response.json();
}

// ============================================
// Version Updates API
// ============================================

/**
 * Get version updates list
 * Admin endpoint: GET /api/admin/version-updates
 *
 * @param {Object} options - Query options
 * @returns {Promise<Object>} Version updates list
 */
export async function getVersionUpdates(options = {}) {
  const authHeaders = await getAuthHeaders();
  const params = new URLSearchParams();

  if (options.limit) params.append('limit', options.limit);
  if (options.offset) params.append('offset', options.offset);

  const url = `${API_URL}/api/admin/version-updates${params.toString() ? '?' + params.toString() : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch version updates');
  }

  return response.json();
}

/**
 * Get version update statistics
 * Admin endpoint: GET /api/admin/version-updates/stats
 *
 * @returns {Promise<Object>} Version statistics
 */
export async function getVersionStats() {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/admin/version-updates/stats`, {
    method: 'GET',
    headers: authHeaders
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch version statistics');
  }

  return response.json();
}

/**
 * Add a new version update entry
 * Admin endpoint: POST /api/admin/version-updates
 *
 * @param {Object} updateData - Version update data
 * @returns {Promise<Object>} Created version update
 */
export async function addVersionUpdate(updateData) {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/admin/version-updates`, {
    method: 'POST',
    headers: authHeaders,
    body: JSON.stringify(updateData)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to add version update');
  }

  return response.json();
}

/**
 * Update an existing version update entry
 * Admin endpoint: PUT /api/admin/version-updates/:updateId
 *
 * @param {string} updateId - Version update ID
 * @param {Object} updateData - Fields to update
 * @returns {Promise<Object>} Updated version update
 */
export async function updateVersionUpdate(updateId, updateData) {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/admin/version-updates/${updateId}`, {
    method: 'PUT',
    headers: authHeaders,
    body: JSON.stringify(updateData)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update version update');
  }

  return response.json();
}

/**
 * Delete a version update entry
 * Admin endpoint: DELETE /api/admin/version-updates/:updateId
 *
 * @param {string} updateId - Version update ID
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteVersionUpdate(updateId) {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/admin/version-updates/${updateId}`, {
    method: 'DELETE',
    headers: authHeaders
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to delete version update');
  }

  return response.json();
}

// ============================================
// Telegram Stats (Admin)
// ============================================

/**
 * Get Telegram bot usage statistics
 * Admin endpoint: GET /api/admin/telegram-stats
 *
 * @returns {Promise<Object>} { data: { rows, totalUsers, totalIncoming, totalOutgoing } }
 */
export async function getTelegramStats() {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/admin/telegram-stats`, {
    method: 'GET',
    headers: authHeaders
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch Telegram stats');
  }

  return response.json();
}
